import { useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCanViewCost } from '@/hooks/use-analytics-v2';
import { useAnalyticsSelection } from '@/modules/analytics-v2/use-analytics-selection';
import { SENTINEL_ANALYTICS_PATHS } from '@/constants/sentinel-analytics-paths';
import { SecondarySidebarLayout } from './secondary-sidebar-layout';

interface Page {
  label: string;
  path: string;
}

/** A sidebar section: a row that folds its pages, which are grouped under headings or listed straight off the trunk. */
interface Section {
  label: string;
  groups: { heading?: string; items: Page[] }[];
}

const SENTINEL: Section = {
  label: 'Sentinel Analytics',
  groups: [
    {
      heading: 'Support',
      items: [
        { label: 'Overview', path: SENTINEL_ANALYTICS_PATHS.SUPPORT_OVERVIEW },
        { label: 'Performance', path: SENTINEL_ANALYTICS_PATHS.SUPPORT_PERFORMANCE },
      ],
    },
    {
      heading: 'Ticket Inflow',
      items: [
        { label: 'Overview', path: SENTINEL_ANALYTICS_PATHS.INFLOW_OVERVIEW },
        { label: 'Message Metrics', path: SENTINEL_ANALYTICS_PATHS.INFLOW_MESSAGE_METRICS },
        { label: 'Ticket Metrics', path: SENTINEL_ANALYTICS_PATHS.INFLOW_TICKET_METRICS },
      ],
    },
  ],
};

const INTERNAL_PAGES: Page[] = [
  { label: 'Message Metrics', path: '/analytics/internal/message-metrics' },
  { label: 'Ticket Metrics', path: '/analytics/internal/ticket-metrics' },
  { label: 'Coverage Funnel', path: '/analytics/internal/coverage-funnel' },
];
const AI_COST: Page = { label: 'AI Cost', path: '/analytics/internal/ai-cost' };

// The Sentinel tables' tree: a trunk under the label; a group's heading branches off it, the last one ends it.
const TRUNK = 'relative pl-3 before:absolute before:left-0 before:top-0 before:bottom-0 before:w-px before:bg-gray-500';
const BRANCH = `${TRUNK} last:before:bottom-auto last:before:h-4 after:absolute after:left-0 after:top-4 after:h-px after:w-2 after:bg-gray-500`;

/**
 * The Overview list spans accounts, so it has no sidebar; an account's pages get one listing
 * its Internal and Sentinel pages once an account is picked.
 */
export function AnalyticsLayout() {
  const { pathname } = useLocation();
  const [{ account }] = useAnalyticsSelection();
  // AI Cost is listed only for emails on the cost allowlist, as its page and the cost API require.
  const canViewCost = useCanViewCost();
  const [closed, setClosed] = useState<Record<string, boolean>>({});

  if (!account || pathname === '/analytics') return <Outlet />;

  const internal: Section = { label: 'Internal Analytics', groups: [{ items: canViewCost ? [...INTERNAL_PAGES, AI_COST] : INTERNAL_PAGES }] };
  // kim-ai-frontend's Sentinel Analytics sidebar rows; hover uses gray-200, as neutral-100 matches this background.
  const row = (active: boolean) =>
    cn(
      'flex w-full items-center gap-1.5 px-2 py-1.5 rounded-2lg text-body-sm text-left transition-colors duration-200 border',
      active ? 'bg-white border-gray-100 shadow-[0_0_8px_0_rgba(0,0,0,0.04)] text-ink' : 'text-gray-900 hover:bg-gray-200 hover:text-ink border-transparent'
    );
  const pageLink = (page: Page) => (
    <Link to={page.path} className={row(pathname === page.path)}>
      {page.label}
    </Link>
  );

  return (
    <SecondarySidebarLayout title={account.name ?? `Account ${account.id}`}>
      <nav className='flex flex-col gap-1'>
        {[internal, SENTINEL].map((section) => {
          const open = !closed[section.label];
          return (
            <div key={section.label} className='flex flex-col'>
              <button type='button' aria-expanded={open} onClick={() => setClosed((c) => ({ ...c, [section.label]: open }))} className={row(false)}>
                {section.label}
                <ChevronRight aria-hidden='true' className={cn('ml-auto size-3.5 shrink-0 text-gray-700 transition-transform duration-200', open && 'rotate-90')} />
              </button>
              {/* Grid rows slide the tree open and shut; inert keeps closed links out of the tab order. */}
              <div
                inert={!open}
                className={cn('grid transition-[grid-template-rows,opacity] duration-300 ease-out', open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0')}
              >
                <ul className='ml-3 flex min-h-0 flex-col overflow-hidden'>
                  {section.groups.map((group) =>
                    group.heading ? (
                      <li key={group.heading} className={BRANCH}>
                        <p className='px-2 pt-2 pb-1 text-xs leading-4 font-medium tracking-tight text-gray-700'>{group.heading}</p>
                        <ul className='flex flex-col gap-1'>
                          {group.items.map((page) => <li key={page.path}>{pageLink(page)}</li>)}
                        </ul>
                      </li>
                    ) : (
                      <li key='pages' className={cn(TRUNK, 'pt-1')}>
                        <ul className='flex flex-col gap-1'>
                          {group.items.map((page) => <li key={page.path}>{pageLink(page)}</li>)}
                        </ul>
                      </li>
                    )
                  )}
                </ul>
              </div>
            </div>
          );
        })}
      </nav>
    </SecondarySidebarLayout>
  );
}
